import { api, type ThemeBranding, type ThemeDTO, type ThemeTokens } from '@/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { useQuery } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react'

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────
type ThemeContextValue = {
  theme: ThemeDTO | null
  isLoading: boolean
}

const ThemeContext = createContext<ThemeContextValue>({ theme: null, isLoading: false })

const DEFAULT_BRANDING: ThemeBranding = {
  company_name: 'AMANET',
  logo_light_url: '/logo.svg',
  logo_dark_url: '/logo.svg',
  favicon_url: null,
}

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────
export function ThemeProvider({ children }: { children: ReactNode }) {
  const userTheme = useUIStore((s) => s.theme)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Fetch the tenant theme only when authenticated.
  // staleTime is high — the theme rarely changes; invalidation happens via the
  // `tenant.theme.updated` event when M11 wires up websockets.
  const { data: tenantTheme, isLoading } = useQuery({
    queryKey: ['theme', 'current'],
    queryFn: () => api.theme.getCurrent(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  })

  // Effective color mode: tenant overrides → user pref → system
  const effectiveMode = resolveColorMode(userTheme, tenantTheme ?? null)

  // Apply .dark class on <html>
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')

    const apply = () => {
      const mode = effectiveMode === 'system' ? (media.matches ? 'dark' : 'light') : effectiveMode
      document.documentElement.classList.toggle('dark', mode === 'dark')
    }

    apply()
    if (effectiveMode === 'system') {
      media.addEventListener('change', apply)
      return () => media.removeEventListener('change', apply)
    }
    return undefined
  }, [effectiveMode])

  // Inject tenant CSS variables into :root when theme loads or color mode changes.
  useEffect(() => {
    if (!tenantTheme?.tokens) return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const isDark =
      effectiveMode === 'dark' || (effectiveMode === 'system' && media.matches)
    const palette: Record<string, string> = isDark
      ? tenantTheme.tokens.dark ?? {}
      : tenantTheme.tokens.light ?? {}

    const root = document.documentElement
    for (const [key, value] of Object.entries(palette)) {
      if (value) root.style.setProperty(`--color-${key}`, value)
    }

    return () => {
      // No cleanup — leaving vars on :root prevents flicker between updates.
      // They are overwritten on the next effect run.
    }
  }, [tenantTheme, effectiveMode])

  // Update favicon if the tenant supplies one.
  useEffect(() => {
    const url = tenantTheme?.branding?.favicon_url
    if (!url) return
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = url
  }, [tenantTheme?.branding?.favicon_url])

  const ctx = useMemo<ThemeContextValue>(
    () => ({ theme: tenantTheme ?? null, isLoading }),
    [tenantTheme, isLoading],
  )

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>
}

// ─────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────

/** Returns the full tenant theme (tokens + branding + prefs) or null. */
export function useTheme() {
  return useContext(ThemeContext).theme
}

/** Returns tenant branding (logo, name, legal). Falls back to AMANET defaults. */
export function useBranding(): ThemeBranding {
  const theme = useTheme()
  return theme?.branding ?? DEFAULT_BRANDING
}

/** Returns the current color tokens (light or dark depending on color mode). */
export function useThemeTokens(): ThemeTokens | null {
  return useTheme()?.tokens ?? null
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

type EffectiveMode = 'light' | 'dark' | 'system'

/**
 * Tenant overrides take priority:
 * - `force_dark_mode = true`         → forced dark
 * - `dark_mode_enabled = false`      → forced light (toggle disabled)
 * - otherwise                        → user preference
 */
function resolveColorMode(
  userPref: 'light' | 'dark' | 'system',
  tenant: ThemeDTO | null,
): EffectiveMode {
  if (tenant?.force_dark_mode) return 'dark'
  if (tenant && tenant.dark_mode_enabled === false) return 'light'
  return userPref
}
